import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X, Calendar, Building, FileText, Loader2, ArrowLeft, LogIn, TrendingUp, Users, Clock } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { SiteOffice } from '@/services/siteOfficeService';
import { supabase } from '@/integrations/supabase/client';
import { Tables } from '@/integrations/supabase/types';
import { useSiteOffice } from '@/contexts/SiteOfficeContext';
import { useYear } from '@/contexts/YearContext';

interface SiteOfficeProjectOverviewProps {
  siteOffice: SiteOffice;
  onClose: () => void;
  onEnterSiteOffice?: () => void;
  isFullPage?: boolean;
}

type ProjectData = Tables<'projects'>;

interface OverviewStats {
  totalProjects: number;
  completedProjects: number;
  inProgressProjects: number;
  delayedProjects: number;
  averageProgress: number;
  projectsByType: { [key: string]: number };
}

const SiteOfficeProjectOverview: React.FC<SiteOfficeProjectOverviewProps> = ({ 
  siteOffice, 
  onClose, 
  onEnterSiteOffice,
  isFullPage = false 
}) => {
  const { selectedYear: globalYear, setSelectedYear: setGlobalYear, yearOptions } = useYear();
  const [selectedYear, setSelectedYear] = useState<string>(globalYear.toString());
  const [projects, setProjects] = useState<ProjectData[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<OverviewStats>({
    totalProjects: 0,
    completedProjects: 0,
    inProgressProjects: 0,
    delayedProjects: 0,
    averageProgress: 0,
    projectsByType: {}
  });

  const { setSiteOffice } = useSiteOffice();

  useEffect(() => {
    fetchProjectData();
  }, [siteOffice.id, selectedYear]);

  const fetchProjectData = async () => {
    try {
      setLoading(true);
      console.log(`🔍 Fetching projects for site office: ${siteOffice.name} (${siteOffice.id}) for year ${selectedYear}`);

      const { data: projectsData, error } = await supabase
        .from('projects')
        .select('*')
        .eq('site_office_id', siteOffice.id)
        .gte('created_at', `${selectedYear}-01-01`)
        .lt('created_at', `${parseInt(selectedYear) + 1}-01-01`)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching projects:', error);
        return;
      }

      console.log(`📊 Found ${projectsData?.length || 0} projects for ${siteOffice.name} in ${selectedYear}`);
      
      const projects = projectsData || [];
      setProjects(projects);

      // Calculate statistics
      const totalProjects = projects.length;
      const completedProjects = projects.filter(p => p.status === 'Complete').length;
      const inProgressProjects = projects.filter(p => p.status === 'In Progress').length;
      const delayedProjects = projects.filter(p => p.status === 'Delayed').length;
      const averageProgress = totalProjects > 0 
        ? projects.reduce((sum, p) => sum + (p.progress || 0), 0) / totalProjects 
        : 0;

      // Group by project type
      const projectsByType = projects.reduce((acc, project) => {
        const type = project.project_type || 'Unknown';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {} as { [key: string]: number });

      setStats({
        totalProjects,
        completedProjects,
        inProgressProjects,
        delayedProjects,
        averageProgress,
        projectsByType
      });

    } catch (error) {
      console.error('Error in fetchProjectData:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEnterSiteOffice = () => {
    // Save selected year to global context when entering
    setGlobalYear(parseInt(selectedYear));
    setSiteOffice(siteOffice.name);
    if (onEnterSiteOffice) {
      onEnterSiteOffice();
    }
  };

  const getStatusColor = (status: string | null) => {
    switch (status) {
      case 'Complete': return 'bg-green-100 text-green-800';
      case 'In Progress': return 'bg-blue-100 text-blue-800';
      case 'Delayed': return 'bg-red-100 text-red-800';
      case 'On Hold': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 80) return 'text-green-600';
    if (progress >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div
      className="bg-white rounded-2xl shadow-xl shadow-slate-200/60 border border-slate-100 overflow-hidden"
      style={{ fontFamily: "'Manrope', system-ui, sans-serif" }}
    >
      {/* Header — Corporate Ocean navy (match login branding panel) */}
      <div className="relative overflow-hidden bg-[#062a4a] p-4 sm:p-5 lg:p-6">
        {/* Dot pattern + ambient glows */}
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, #fff 1px, transparent 0)',
              backgroundSize: '40px 40px',
            }}
          />
          <div className="absolute -top-[40%] -right-[5%] w-[320px] h-[320px] rounded-full bg-[#0ea5e9]/30 blur-3xl" />
        </div>

        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-9 w-9 sm:h-10 sm:w-10 bg-white/15 hover:bg-white/25 text-white rounded-full flex-shrink-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="w-11 h-11 sm:w-12 sm:h-12 bg-white/15 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0">
              <Building className="w-6 h-6 text-[#38bdf8]" />
            </div>
            <div className="min-w-0">
              <h2
                className="text-lg sm:text-xl lg:text-2xl font-extrabold tracking-tight text-white truncate"
                style={{ fontFamily: "'Sora', system-ui, sans-serif" }}
              >
                {siteOffice.name} <span className="text-[#0ea5e9]">.</span>
              </h2>
              <p className="text-[#e0f2fe]/70 text-xs sm:text-sm">{siteOffice.location || 'Project Overview'}</p>
            </div>
          </div>

          {/* Enter button — selalu terlihat tanpa scroll */}
          <Button
            onClick={handleEnterSiteOffice}
            className="bg-[#0284c7] hover:bg-[#0369a1] text-white font-semibold px-5 sm:px-6 rounded-xl shadow-lg shadow-sky-900/30 hover:shadow-xl transition-all duration-300 w-full sm:w-auto flex-shrink-0"
          >
            <LogIn className="w-4 h-4 mr-2" />
            Masuk ke Site Office
          </Button>
        </div>
      </div>

      {/* Year Selection Bar */}
      <div className="px-4 sm:px-6 lg:px-8 py-3 border-b border-gray-100 bg-gray-50/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-gray-500" />
            <span className="font-medium text-gray-700">Tahun:</span>
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-32 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="text-sm text-gray-500">
            {stats.totalProjects} proyek ditemukan
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 sm:p-6 lg:p-7 space-y-5 sm:space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-10 h-10 animate-spin text-[#0284c7]" />
            <span className="ml-4 text-lg text-gray-600">Memuat data proyek...</span>
          </div>
        ) : (
          <>
            {/* Statistics Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200">
                <div className="flex items-center gap-2.5 mb-2">
                  <div className="w-9 h-9 bg-blue-500 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-sm font-medium text-blue-700">Total Proyek</span>
                </div>
                <div className="text-2xl lg:text-3xl font-bold text-blue-600">{stats.totalProjects}</div>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl border border-green-200">
                <div className="flex items-center gap-2.5 mb-2">
                  <div className="w-9 h-9 bg-green-500 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-sm font-medium text-green-700">Selesai</span>
                </div>
                <div className="text-2xl lg:text-3xl font-bold text-green-600">{stats.completedProjects}</div>
              </div>

              <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-4 rounded-xl border border-yellow-200">
                <div className="flex items-center gap-2.5 mb-2">
                  <div className="w-9 h-9 bg-yellow-500 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-sm font-medium text-yellow-700">Berjalan</span>
                </div>
                <div className="text-2xl lg:text-3xl font-bold text-yellow-600">{stats.inProgressProjects}</div>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-red-100 p-4 rounded-xl border border-red-200">
                <div className="flex items-center gap-2.5 mb-2">
                  <div className="w-9 h-9 bg-red-500 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-sm font-medium text-red-700">Terlambat</span>
                </div>
                <div className="text-2xl lg:text-3xl font-bold text-red-600">{stats.delayedProjects}</div>
              </div>
            </div>

            {/* Average Progress */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 sm:p-5 rounded-xl border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <span className="text-base sm:text-lg font-semibold text-gray-700">Rata-rata Progress</span>
                <span className={`text-xl sm:text-2xl font-bold ${getProgressColor(stats.averageProgress)}`}>
                  {Math.round(stats.averageProgress)}%
                </span>
              </div>
              <Progress value={stats.averageProgress} className="h-3" />
            </div>

            <div className="grid lg:grid-cols-2 gap-5 sm:gap-6">
              {/* Project Types */}
              {Object.keys(stats.projectsByType).length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5">
                  <h4 className="font-semibold text-gray-800 mb-3 text-base sm:text-lg">Proyek Berdasarkan Tipe</h4>
                  <div className="space-y-2.5">
                    {Object.entries(stats.projectsByType).map(([type, count]) => (
                      <div key={type} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <span className="text-gray-700 font-medium">{type}</span>
                        <Badge variant="secondary" className="text-sm px-3 py-1">{count}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Projects */}
              {projects.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5">
                  <h4 className="font-semibold text-gray-800 mb-3 text-base sm:text-lg">Proyek Terbaru</h4>
                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    {projects.slice(0, 8).map((project) => (
                      <div key={project.id} className="p-4 border border-gray-100 rounded-lg bg-gray-50/50 hover:bg-gray-50 transition-colors">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h5 className="font-medium text-gray-800">{project.title}</h5>
                            <p className="text-gray-500 text-sm">#{project.project_number}</p>
                          </div>
                          <Badge className={`${getStatusColor(project.status)} text-xs`}>
                            {project.status || 'Unknown'}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Progress value={project.progress || 0} className="w-24 h-2" />
                            <span className="text-gray-600 text-sm">{project.progress || 0}%</span>
                          </div>
                          <div className="text-gray-500 text-sm">{project.pic || 'No PIC'}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* No Projects Message */}
            {projects.length === 0 && (
              <div className="text-center py-16 bg-gray-50 rounded-xl border border-gray-200">
                <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-xl text-gray-500 font-medium">Tidak ada proyek untuk tahun {selectedYear}</p>
                <p className="text-gray-400 mt-2">Coba pilih tahun yang berbeda</p>
              </div>
            )}

            {/* Bottom Action */}
            <div className="flex justify-center pt-2">
              <Button
                onClick={handleEnterSiteOffice}
                size="lg"
                className="bg-[#0284c7] hover:bg-[#0369a1] text-white font-semibold px-10 py-4 rounded-xl shadow-lg shadow-sky-900/20 hover:shadow-xl transition-all duration-300 transform hover:scale-105 w-full sm:w-auto"
              >
                <LogIn className="w-5 h-5 mr-3" />
                Masuk ke Site Office
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SiteOfficeProjectOverview;